import React from "react";
import { Link } from "react-router-dom";
import { Plane, Clock, Zap, Heart, Columns3, Wallet } from "lucide-react";
import { airlineLogo, fmtPoints, fmtDate, CABINS } from "../data/mock";
import { useWishlist } from "../context/WishlistContext";
import { useWallet } from "../context/WalletContext";
import { useToast } from "../hooks/use-toast";
import ShareButton from "./ShareButton";
import BookButtons from "./BookButtons";

const cabinLabel = (k) => (CABINS.find((c) => c.key === k) || {}).label || k;
const cabinColor = {
  economy: "bg-slate-100 text-slate-700",
  premium: "bg-amber-100 text-amber-800",
  business: "bg-blue-100 text-blue-700",
  first: "bg-purple-100 text-purple-700",
};

function SaveButton({ deal }) {
  const wishlist = useWishlist();
  const { toast } = useToast();
  const saved = wishlist ? wishlist.isSaved(deal.id) : false;

  const onSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!wishlist) return;
    const res = await wishlist.toggle(deal);
    if (res?.needAuth) toast({ title: "Sign in to save deals", description: "Create a free account to build your wishlist." });
    else if (res?.added) toast({ title: "Saved to wishlist", description: `${deal.origin.code} → ${deal.destination.code} added.` });
  };

  return (
    <button
      onClick={onSave}
      aria-label="Save deal"
      data-testid="wishlist-save-button"
      className={`absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
        saved ? "border-red-200 bg-red-50 text-red-500" : "border-slate-200 bg-white text-slate-400 hover:text-red-500"
      }`}
    >
      <Heart className="h-4 w-4" fill={saved ? "currentColor" : "none"} />
    </button>
  );
}

function RouteRow({ d }) {
  return (
    <div className="mt-4 flex items-center justify-between">
      <div className="text-center">
        <p className="text-base font-bold text-slate-900">{d.origin.code}</p>
        <p className="text-[11px] text-slate-500">{d.origin.city}</p>
      </div>
      <div className="flex flex-1 flex-col items-center px-2">
        <div className="flex w-full items-center gap-1">
          <div className="h-px flex-1 bg-slate-200" />
          <Plane className="h-3.5 w-3.5 rotate-90 text-blue-500" />
          <div className="h-px flex-1 bg-slate-200" />
        </div>
        <span className="mt-1 flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" /> {d.duration}</span>
      </div>
      <div className="text-center">
        <p className="text-base font-bold text-slate-900">{d.destination.code}</p>
        <p className="text-[11px] text-slate-500">{d.destination.city}</p>
      </div>
    </div>
  );
}

function AffordBadge({ deal }) {
  const wallet = useWallet();
  const a = wallet?.affordability(deal);
  if (!a) return null;
  return a.ok ? (
    <span data-testid="afford-badge-ok" className="mt-2 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-bold text-green-700"><Wallet className="h-3 w-3" /> You can afford this</span>
  ) : (
    <span data-testid="afford-badge-short" className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"><Wallet className="h-3 w-3" /> {fmtPoints(a.missing)} more pts needed</span>
  );
}

function PriceRow({ d, showFresh }) {
  return (
    <div className="mt-4 flex items-end justify-between border-t border-slate-100 pt-3">
      <div>
        <p className="text-lg font-extrabold text-blue-600">{fmtPoints(d.points)} <span className="text-xs font-semibold text-slate-500">pts</span></p>
        <p className="text-[11px] text-slate-500">+ ${d.taxes.amount} {d.taxes.currency}</p>
      </div>
      <div className="flex items-center gap-1.5">
        {showFresh && d.fresh && (
          <span className="flex items-center gap-0.5 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700"><Zap className="h-2.5 w-2.5" /> Fresh</span>
        )}
        {d.seats && <span className="text-[11px] text-slate-400">{d.seats} seat{d.seats > 1 ? "s" : ""}</span>}
      </div>
    </div>
  );
}

export default function FlightCard({ d, showFresh = false, savable = true }) {
  return (
    <div className="deal-card relative flex flex-col rounded-xl border border-slate-200 bg-white p-4">
      {savable && <SaveButton deal={d} />}
      <div className="flex items-center justify-between pr-9">
        <span className="text-xs font-medium text-slate-500">{fmtDate(d.date)}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cabinColor[d.cabin] || cabinColor.economy}`}>{cabinLabel(d.cabin)}</span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <img src={airlineLogo(d.airlineCode)} alt={d.airline} className="h-8 w-8 rounded-md object-contain" onError={(e) => { e.target.style.visibility = "hidden"; }} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{d.airline}</p>
          <p className="text-xs text-slate-500">{d.program}</p>
        </div>
      </div>
      <RouteRow d={d} />
      <PriceRow d={d} showFresh={showFresh} />
      <AffordBadge deal={d} />
      <div className="mt-3"><BookButtons deal={d} /></div>
      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
        <Link to={`/cabin-compare?from=${d.origin.code}&to=${d.destination.code}&date=${d.date}`} data-testid="compare-cabins-link" className="flex min-h-9 items-center gap-1 text-xs font-semibold text-blue-600 hover:underline">
          <Columns3 className="h-3.5 w-3.5" /> Compare cabins
        </Link>
        <ShareButton deal={d} />
      </div>
    </div>
  );
}
